import { Plus, Code, Eye, Trash2, ChevronRight, ChevronDown, Asterisk, CheckCircle, Square, CheckSquare, Info, LayoutTemplate, FileJson } from "lucide-react";
import { useEffect, useState, memo } from "react";
import { toast } from "react-hot-toast";
import Tooltip from "../../widgets/ToolTip";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../widgets/Breadcurmbs";

// Interfaces
interface TemplateFormProps {
  template?: any;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

interface EnumValue {
  id: string;
  value: string;
}

interface Property {
  id: string;
  name: string;
  type: string;
  required: boolean;
  description: string;
  singleValue: boolean;
  children?: Property[];
  isExpanded?: boolean;
  enumValues?: EnumValue[];
}

interface TemplateFormData {
  schema_name: string;
  schema_description: string;
  properties: Property[];
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}


// --- Component for a single Property Row ---
const PropertyRow = memo(({
  property,
  level = 0,
  updateProperty,
  removeProperty,
  addProperty,
  toggleExpand,
}: {
  property: Property;
  level?: number;
  updateProperty: (id: string, field: keyof Property, value: any) => void;
  removeProperty: (id: string) => void;
  addProperty: (parentId?: string) => void;
  toggleExpand: (id: string) => void;
}) => {
  const hasChildren = property.type === "object" || property.type === "array";

  const handleAddEnumValue = () => {
    const newEnumValue: EnumValue = { id: Date.now().toString() + Math.random(), value: "" };
    const updatedEnumValues = [...(property.enumValues || []), newEnumValue];
    updateProperty(property.id, "enumValues", updatedEnumValues);
  };

  const handleUpdateEnumValue = (enumId: string, newValue: string) => {
    const updatedEnumValues = (property.enumValues || []).map(enumItem =>
      enumItem.id === enumId ? { ...enumItem, value: newValue } : enumItem
    );
    updateProperty(property.id, "enumValues", updatedEnumValues);
  };

  const handleRemoveEnumValue = (enumId: string) => {
    const updatedEnumValues = (property.enumValues || []).filter(enumItem => enumItem.id !== enumId);
    updateProperty(property.id, "enumValues", updatedEnumValues);
  };


  return (
    <div
      className="bg-gray-50 border border-gray-200 rounded-lg"
      style={{ marginLeft: level > 0 ? "24px" : "0px" }}
    >
      {/* Main Row for inputs and actions */}
      <div className="flex items-center gap-2 p-3">
        <div className="w-6">
          {hasChildren && (
            <button
              onClick={() => toggleExpand(property.id)}
              className="p-1 text-gray-500 rounded hover:bg-gray-200"
              type="button"
            >
              {property.isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        <input
          type="text"
          placeholder="Property name"
          value={property.name}
          onChange={(e) => updateProperty(property.id, "name", e.target.value)}
          className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <select
          value={property.type}
          onChange={(e) => updateProperty(property.id, "type", e.target.value)}
          className="w-32 px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="string">String</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="enum">Enum</option>
          <option value="array">Array</option>
          <option value="object">Object</option>
        </select>

        <button
          onClick={() => updateProperty(property.id, "required", !property.required)}
          type="button"
          className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${property.required
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-600 hover:bg-blue-100"
            }`}
        >
          <Tooltip content="Required">
            <Asterisk className="w-4 h-4" />
          </Tooltip>
        </button>

        <button
          onClick={() => updateProperty(property.id, "singleValue", !property.singleValue)}
          type="button"
          className={`flex items-center justify-center w-8 h-8 rounded transition-colors ${property.singleValue
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-600 hover:bg-blue-100"
            }`}
        >
          <Tooltip content="Make property a single value" position="left">
            {property.singleValue ? (
              <CheckSquare className="w-4 h-4" />
            ) : (
              <Square className="w-4 h-4" />
            )}
          </Tooltip>
        </button>

        <button
          onClick={() => removeProperty(property.id)}
          type="button"
          className="w-8 h-8 text-gray-500 rounded-md hover:bg-red-50 hover:text-red-600"
        >
          <Tooltip content="Delete">
            <Trash2 className="w-4 h-4" />
          </Tooltip>
        </button>
      </div>

      {/* Dynamic list for Enum Values */}
      {property.type === "enum" && (
        <div className="px-3 pb-3 pl-12 space-y-2">
          {property.enumValues?.map((enumItem) => (
            <div key={enumItem.id} className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Enum value"
                value={enumItem.value}
                onChange={(e) => handleUpdateEnumValue(enumItem.id, e.target.value)}
                className="flex-grow px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <Tooltip content="Delete">
              <button
                onClick={() => handleRemoveEnumValue(enumItem.id)}
                type="button"
                className="w-7 h-7 flex-shrink-0 text-gray-400 rounded-md hover:bg-red-50 hover:text-red-500 flex items-center justify-center"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              </Tooltip>
            </div>
          ))}
          <button
            onClick={handleAddEnumValue}
            className="flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-800"
            type="button"
          >
            <Plus className="w-3.5 h-3.5" />
            Add enum value
          </button>
        </div>
      )}

      {/* Nested Children and "Add Nested" button */}
      {hasChildren && property.isExpanded && (
        <div className="p-3 pt-0 pl-12">
          <div className="space-y-3">
            {property.children?.map((child) => (
              <PropertyRow
                key={child.id}
                property={child}
                level={0}
                updateProperty={updateProperty}
                removeProperty={removeProperty}
                addProperty={addProperty}
                toggleExpand={toggleExpand}
              />
            ))}
          </div>
          <button
            onClick={() => addProperty(property.id)}
            className="flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-800"
            type="button"
          >
            <Plus className="w-3.5 h-3.5" />
            Add nested property
          </button>
        </div>
      )}
    </div>
  );
});

// --- Main Page Component ---
const TemplateForm = ({
  template,
  onSubmit,
  isLoading = false,
}: TemplateFormProps) => {
  const [activeTab, setActiveTab] = useState<"visual" | "code">("visual");
  const [formData, setFormData] = useState<TemplateFormData>({
    schema_name: "",
    schema_description: "",
    properties: [],
  });
  const [nameError, setNameError] = useState("");
  const [descError, setDescError] = useState("");
  const [codeEditorValue, setCodeEditorValue] = useState<string>("");
  const navigate = useNavigate();
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: "Template list",
      icon: <LayoutTemplate className="w-4 h-4" />,
      path: "/templates",
    },
    {
      label: template ? "Edit template" : "Add template",
      icon: <FileJson className="w-4 h-4" />,
    },
  ];

  // Universal schema parser (for initial load and code editor)
  const parseSchemaToProperties = (schema: any, requiredFields: string[] = []): Property[] => {
    if (!schema || !schema.properties) return [];
    const propertyOrder = schema.propertyOrdering || Object.keys(schema.properties);

    return propertyOrder
      .map((key: string) => {
        const value = schema.properties[key];
        if (!value) return null;

        let actualType: string;
        let children: Property[] | undefined = undefined;
        let enumValues: EnumValue[] | undefined = undefined;
        let isSingleValue = false;

        // Case 1: Array of objects
        if (value.type === "array" && value.items?.properties) {
          actualType = "array";
          children = parseSchemaToProperties(value.items, value.items.required || []);
        }
        // Case 2: Simple object
        else if (value.type === "object" && value.properties) {
          actualType = "object";
          children = parseSchemaToProperties(value, value.required || []);
        }
        // Case 3: Array of primitives (string, number, boolean, or enum)
        else if (value.type === "array" && value.items) {
          isSingleValue = true;
          if (Array.isArray(value.items.enum)) {
            actualType = "enum";
            enumValues = value.items.enum.map((val: string) => ({ id: Math.random().toString(), value: val }));
          } else {
            actualType = value.items.type || "string";
          }
        }
        // Case 4: Simple enum
        else if (Array.isArray(value.enum)) {
          actualType = "enum";
          enumValues = value.enum.map((val: string) => ({ id: Math.random().toString(), value: val }));
        }
        // Case 5: Simple primitive (string, number, boolean)
        else {
          actualType = value.type || "string";
        }

        return {
          id: Math.random().toString() + Date.now() + key,
          name: key,
          type: actualType,
          description: value.description || "",
          required: requiredFields.includes(key),
          singleValue: isSingleValue,
          isExpanded: true,
          children,
          enumValues,
        };
      })
      .filter(Boolean) as Property[];
  };

  // Prefill when editing
  useEffect(() => {
    if (template) {
      const outputSchema = template.output_schema_structure || {};
      setFormData({
        schema_name: template.schema_name || "",
        schema_description: template.schema_description || "",
        properties: parseSchemaToProperties(
          outputSchema,
          outputSchema.required || []
        ),
      });
    } else {
      setFormData({
        schema_name: "",
        schema_description: "",
        properties: [],
      });
      setCodeEditorValue(
        '{\n  "type": "object",\n  "properties": {},\n  "propertyOrdering": [],\n  "required": []\n}'
      );
    }
  }, [template]);


  const generateOutputSchema = (properties: Property[]): any => {
    const schema: any = {
      type: "object",
      properties: {},
      propertyOrdering: [],
      required: [],
    };
    properties.forEach((prop) => {
      if (!prop.name.trim()) return;

      schema.propertyOrdering.push(prop.name);
      if (prop.required) schema.required.push(prop.name);

      let propSchema: any = {};

      if (prop.type === "object") {
        propSchema = generateOutputSchema(prop.children || []);
      } else if (prop.type === "array") {
        if (prop.children && prop.children.length > 0) {
          const itemsSchema = generateOutputSchema(prop.children || []);
          propSchema = { type: "array", items: itemsSchema };
        } else {
          propSchema = { type: "array", items: { type: "string" } };
        }
      } else if (prop.type === "enum") {
        propSchema = {
          type: "string",
          enum: prop.enumValues?.map(e => e.value).filter(v => v && v.trim()) || [],
        };
      } else { // string, number, boolean
        propSchema = { type: prop.type };
      }

      // Handle singleValue wrapper for primitive types and enums
      if (prop.singleValue && !['object', 'array'].includes(prop.type)) {
        schema.properties[prop.name] = {
          type: "array",
          items: propSchema,
        };
      } else {
        schema.properties[prop.name] = propSchema;
      }
    });

    if (schema.required.length === 0) {
      delete schema.required;
    }
    return schema;
  };
  useEffect(() => {
    const generatedSchema = generateOutputSchema(formData.properties);
    setCodeEditorValue(JSON.stringify(generatedSchema, null, 2));
  }, [formData.properties]);

  const addProperty = (parentId?: string) => {
    const newProperty: Property = {
      id: Date.now().toString() + Math.random(),
      name: "",
      type: "string",
      required: false,
      singleValue: false,
      description: "",
      isExpanded: true,
      enumValues: [],
    };

    if (!parentId) {
      setFormData((prev) => ({
        ...prev,
        properties: [...prev.properties, newProperty],
      }));
    } else {
      const addToParent = (props: Property[]): Property[] => {
        return props.map((prop) => {
          if (prop.id === parentId) {
            return {
              ...prop,
              children: [...(prop.children || []), newProperty],
            };
          }
          if (prop.children) {
            return { ...prop, children: addToParent(prop.children) };
          }
          return prop;
        });
      };
      setFormData((prev) => ({
        ...prev,
        properties: addToParent(prev.properties),
      }));
    }
  };

  const removeProperty = (id: string) => {
    const removeFromTree = (props: Property[]): Property[] => {
      return props
        .filter((prop) => prop.id !== id)
        .map((prop) => ({
          ...prop,
          children: prop.children ? removeFromTree(prop.children) : undefined,
        }));
    };
    setFormData((prev) => ({
      ...prev,
      properties: removeFromTree(prev.properties),
    }));
  };

  const updateProperty = (id: string, field: keyof Property, value: any) => {
    const updateInTree = (props: Property[]): Property[] => {
      return props.map((prop) => {
        if (prop.id === id) {
          const updated = { ...prop, [field]: value };
          if (field === "type") {
            if (value !== "object" && value !== "array") {
              updated.children = undefined;
            }
            if (value === "object" || value === "array") {
              if (!updated.children) updated.children = [];
              updated.singleValue = false;
            }
            if (value === "enum") {
              if (!updated.enumValues) updated.enumValues = [];
            } else {
              updated.enumValues = undefined;
            }
          }
          return updated;
        }
        if (prop.children) {
          return { ...prop, children: updateInTree(prop.children) };
        }
        return prop;
      });
    };
    setFormData((prev) => ({
      ...prev,
      properties: updateInTree(prev.properties),
    }));
  };

  const toggleExpand = (id: string) => {
    const toggleInTree = (props: Property[]): Property[] => {
      return props.map((prop) => {
        if (prop.id === id) {
          return { ...prop, isExpanded: !prop.isExpanded };
        }
        if (prop.children) {
          return { ...prop, children: toggleInTree(prop.children) };
        }
        return prop;
      });
    };
    setFormData((prev) => ({
      ...prev,
      properties: toggleInTree(prev.properties),
    }));
  };

  const handleApplyCodeChanges = () => {
    try {
      const parsedSchema = JSON.parse(codeEditorValue);

      if (typeof parsedSchema !== 'object' || parsedSchema === null) {
        toast.error('Invalid JSON: Schema must be an object.');
        return;
      }

      if (parsedSchema.type !== 'object') {
        toast.error('Invalid JSON: Root type must be "object".');
        return;
      }

      if (!parsedSchema.properties || typeof parsedSchema.properties !== 'object') {
        toast.error('Invalid schema: Missing or invalid "properties" field');
        return;
      }

      const newProperties = parseSchemaToProperties(parsedSchema, parsedSchema.required || []);
      setFormData(prev => ({ ...prev, properties: newProperties }));
      toast.success('Schema applied successfully!');
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error(`JSON Syntax Error: ${error.message}`);
      } else {
        toast.error('Failed to apply code changes. Please check your JSON.');
      }
    }
  };

  const handleSave = () => {
    if (!formData.schema_name.trim()) {
      toast.error('Schema name is required');
      return;
    }

    const payload = {
      schema_name: formData.schema_name,
      schema_description: formData.schema_description,
      output_schema_structure: generateOutputSchema(formData.properties),
    };
    onSubmit(payload);
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
      <div className="px-6 py-4">
      <div>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />
      </div>

      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 via-blue-50/40 to-slate-50 border-b border-slate-200/60 mt-4">
        <div className="flex items-start gap-3 ">
          <div className="flex-shrink-0 mt-1">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Fill in the template name and description, then use the editor below to design your letter. You can format text, add variables, and preview your template before saving.
            </p>
          </div>
        </div>
      </div>
      </div>
      </div>

      <div className="bg-white mb-4 mt-4 px-6 py-4">
        {/* Form Body */}
        <div>
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter Template name"
                value={formData.schema_name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!/^[A-Za-z0-9\s]*$/.test(value)) {
                    setNameError("Only alphabets, numbers, and spaces are allowed");
                    return;
                  }
                  setNameError("");
                  setFormData({ ...formData, schema_name: value });
                }}
                onBlur={() => setNameError("")}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {nameError && (
                <p className="text-red-500 text-sm mt-1">{nameError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Description
              </label>
              <input
                type="text"
                placeholder="Enter Template description"
                value={formData.schema_description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!/^[A-Za-z0-9\s_.,-]*$/.test(value)) {
                    setDescError("Only alphabets, numbers, spaces, hyphens, underscores, commas and periods are allowed");
                    return;
                  }
                  setDescError("");
                  setFormData({ ...formData, schema_description: value });
                }}
                onBlur={() => setDescError("")}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {descError && (
                <p className="text-red-500 text-sm mt-1">{descError}</p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex border-b border-transparent">
              <button
                onClick={() => setActiveTab("code")}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors duration-200 ${activeTab === "code"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-blue-600"
                  }`}
                type="button"
              >
                <Code className="w-5 h-5" />
                Code Editor
              </button>

              <button
                onClick={() => setActiveTab("visual")}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors duration-200 ${activeTab === "visual"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-blue-600"
                  }`}
                type="button"
              >
                <Eye className="w-5 h-5" />
                Visual Editor
              </button>
            </div>
          </div>


          {/* Content */}
          <div className="min-h-[300px]">
            {activeTab === "visual" ? (
              <div>
                <div className="space-y-3 mb-6">
                  {formData.properties.map((property) => (
                    <PropertyRow
                      key={property.id}
                      property={property}
                      updateProperty={updateProperty}
                      removeProperty={removeProperty}
                      addProperty={addProperty}
                      toggleExpand={toggleExpand}
                    />
                  ))}
                </div>
                {formData.properties.length === 0 && (
                  <div className="text-center py-16 text-gray-500">
                    No properties yet. Click "Add Property" to start.
                  </div>
                )}
                <button
                  onClick={() => addProperty()}
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 transition-colors bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200"
                  type="button"
                >
                  <Plus className="w-4 h-4" />
                  Add property
                </button>
              </div>
            ) : (
              <div>
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <textarea
                    value={codeEditorValue}
                    onChange={(e) => setCodeEditorValue(e.target.value)}
                    className="w-full h-96 bg-transparent text-green-400 text-sm font-mono resize-none focus:outline-none"
                    spellCheck={false}
                    placeholder='{\n  "type": "object",\n  "properties": {},\n  "propertyOrdering": [],\n  "required": []\n}'
                  />
                </div>
                <button
                  onClick={handleApplyCodeChanges}
                  className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  type="button"
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply Changes
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 rounded-b-lg mb-3 pt-4">
        <button
  type="button"
  onClick={() => navigate("/templates")}
  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors font-medium"
>
  Cancel
</button>

<button
  onClick={handleSave}
  disabled={isLoading || !formData.schema_name.trim()}
  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
  type="button"
>
  {isLoading ? "Saving..." : template ? "Update Template" : "Save Template"}
</button>

        </div>
      </div>
    </div>
  );
};

export default TemplateForm;